import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCInput, SCSelect } from 'rollup-allspark';
import { cn, isEmpty } from '../../../../utils';
import { readablePrice } from '../../../../utils/getCurrency';
import { updateEntity } from '../../actions';
import { makeSelectProperty } from '../../selector';
import ModifierRow from './ModifierRow';

const ItemRow = ({
  index = null,
  isDisabled = false,
  selectedBranch = {},
  item = {},
  itemList = [],
  cartItems = [],
  updateState,
}) => {
  const {
    unit_price: unitPrice = 0,
    quantity = 0,
    modifiers = [],
    total_price: totalPrice = 0,
  } = item;

  const {
    currency_code: currencyCode = '',
    currency_symbol: currencySymbol = '',
  } = selectedBranch;

  const getTotalPrice = itm => {
    const {
      unit_price: itmUnitPrice,
      quantity: itmQuantity,
      modifiers: itmModifiers,
    } = itm;

    const modTotalPrice = itmModifiers.reduce((acc, mod) => {
      const modPrice = parseFloat(mod.unit_price) * parseFloat(mod.quantity);
      return acc + modPrice;
    }, 0);

    return (
      parseFloat(itmUnitPrice) * parseFloat(itmQuantity) +
      modTotalPrice
    ).toFixed(2);
  };

  const isModAddDisabled =
    isDisabled ||
    modifiers.some(
      modifier =>
        isEmpty(modifier.title) ||
        modifier.unit_price < 0 ||
        modifier.quantity < 1,
    );

  const handleItemChange = (name, value) => {
    const newItems = [...cartItems];
    const updatedItem = {
      ...newItems[index],
      [name]: value,
    };

    newItems[index] = {
      ...updatedItem,
      total_price: getTotalPrice(updatedItem),
    };

    updateState({
      cartItems: newItems,
    });
  };

  const handleChange = e => {
    const { name, value } = e.target;

    handleItemChange(name, value);
  };

  const handleRemoveItem = () => {
    const newItems = cartItems.filter((_, i) => i !== index);

    updateState({
      cartItems: newItems,
    });
  };

  const handleModifierAdd = () => {
    if (isModAddDisabled) return;
    const newModifiers = [
      ...modifiers,
      {
        id: null,
        modId: modifiers.length + 1,
        title: '',
        unit_price: 0,
        quantity: 0,
      },
    ];

    handleItemChange('modifiers', newModifiers);
  };

  const onItemSelect = i => {
    const newItems = [...cartItems];
    const updatedItem = {
      ...newItems[index],
      id: i?.id || null,
      title: i?.title?.en || '',
    };

    newItems[index] = {
      ...updatedItem,
    };

    updateState({
      cartItems: newItems,
    });
  };

  const modifiersEl = (() => {
    if (isEmpty(modifiers)) return null;

    const rowsEl = modifiers.map((modifier, idx) => (
      <ModifierRow
        key={modifier.modId}
        index={idx}
        modifier={modifier}
        modifiers={modifiers}
        currencySymbol={currencySymbol}
        handleModifierChange={values => handleItemChange('modifiers', values)}
      />
    ));

    return <div className="flex flex-col gap-y-2 mt-2">{rowsEl}</div>;
  })();

  return (
    <div className="border-b border-grey pb-4">
      <div className="flex items-center justify-between gap-x-4">
        <div className="flex flex-1 items-center gap-x-4">
          <SCSelect
            title={<div className="text-grey-darker -mt-2">Item</div>}
            searchable
            options={itemList}
            track-by="id"
            hasError={isEmpty(item)}
            customLabel={i => `${i.id}. ${i.title?.en || i.title}`}
            searchAbleAttribute={['id', 'title_en']}
            value={item}
            onSelect={onItemSelect}
            placeholder="Select Item"
            className="w-2/5"
          />
          <div className="sc-input-container w-1/5">
            <label htmlFor="unit_price" className="-mt-2 mb-1">
              Unit Price
            </label>
            <SCInput
              prefix={`${currencySymbol} `}
              type="number"
              placeholder="Unit Price"
              value={unitPrice}
              name="unit_price"
              id="unit_price"
              handleChange={handleChange}
              className="border-grey-dark rounded-lg mt-1"
            />
          </div>
          <SCInput
            name="quantity"
            type="number"
            label="Count"
            className="w-1/5"
            placeholder="Quantity"
            inputClass="border border-grey rounded-lg"
            value={quantity}
            handleChange={handleChange}
          />
          <SCInput
            label="Total Item Price"
            className="w-1/5"
            inputClass="border border-grey rounded-lg"
            value={readablePrice(parseFloat(totalPrice) * 100, {
              code: currencyCode,
              symbol: currencySymbol,
            })}
            readOnly
          />
        </div>
        <button
          type="button"
          onClick={handleRemoveItem}
          className="flex items-center justify-end gap-x-1 p-1"
        >
          <i className="kt-x text-red-dark text-sm" />
          <p className="text-xs text-red-dark font-medium line-clamp-1">
            Remove Item
          </p>
        </button>
      </div>
      <div className="ml-8">
        {modifiersEl}
        <button
          type="button"
          onClick={handleModifierAdd}
          disabled={isModAddDisabled}
          className={cn({
            'flex items-center justify-center p-5 rounded-lg bg-grey gap-x-1 w-fit h-10 mt-2': true,
            'opacity-50': isModAddDisabled,
          })}
        >
          <i className="kt-plus text-lg" />
          <p className="text-xs font-medium">Add Modifier</p>
        </button>
      </div>
    </div>
  );
};

ItemRow.propTypes = {
  cartItems: PropTypes.array,
  index: PropTypes.number,
  isDisabled: PropTypes.bool,
  itemList: PropTypes.array,
  item: PropTypes.object,
  selectedBranch: PropTypes.object,
  updateState: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  cartItems: makeSelectProperty('cartItems'),
  itemList: makeSelectProperty('items'),
  selectedBranch: makeSelectProperty('selectedBranch'),
});

const mapDispatchToProps = dispatch => ({
  updateState: meta => dispatch(updateEntity(meta)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(ItemRow);
